import {
  GetLearnerModeTransferTokenMutation,
  GetLearnerModeTransferTokenMutationVariables,
} from "@outschool/gql-frontend-generated";
import { LearnerRoutes, addParamsToUrl } from "@outschool/routes";
import {
  MutationFunction,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { CurrentUser, useSession } from "@outschool/ui-auth";
import { switchToLearnerModeWithToken } from "@outschool/ui-components-website";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import * as Env from "../../../../shared/Env";
import { getLearnerModeTransferToken } from "../../../queries/AuthenticationQueries";
import { LearnersForParentInSectionQuery } from "../../../queries/LearnersForParentInSectionQuery";
import { useStripeCheckout } from "../../../routes/Subscriptions/hooks/useStripeCheckout";
import AppState from "../../../stores/AppState";
import { useAppState } from "../../../stores/AppStateProvider";

type GetTransferTokenFunction = MutationFunction<
  GetLearnerModeTransferTokenMutation,
  GetLearnerModeTransferTokenMutationVariables
>;

async function switchToLearnerMode(
  getTransferToken: GetTransferTokenFunction,
  appState: AppState,
  learnerUid: string,
  currentUser: CurrentUser,
  sectionUid: string,
) {
  const { data } = await getTransferToken({ variables: { learnerUid } });
  const transferToken = data?.learnerModeTransferToken?.transferToken;
  if (transferToken) {
    switchToLearnerModeWithToken({
      transferToken,
      isReviewApp: Env.isReviewApp,
      isLearnerLogin: true,
      learnerAppUrl: addParamsToUrl(LearnerRoutes.dashboardPath(), {
        reviewsection: sectionUid,
      }),
    });
  }
}

export function useParentDashboardRedirects() {
  const { redirectAfterLogin, stripeIsLoading } = useStripeCheckout();
  const [getTransferToken] = useMutation<
    GetLearnerModeTransferTokenMutation,
    GetLearnerModeTransferTokenMutationVariables
  >(getLearnerModeTransferToken);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const reviewSection = params.get("reviewsection");
  const appState = useAppState();
  const { currentUser } = useSession();
  const { data: learnersData } = useQueryWithPreviousData(
    LearnersForParentInSectionQuery,
    {
      variables: {
        sectionUid: reviewSection,
      },
      skip: !reviewSection,
    },
  );

  // If the reviewSection query param is present, we query to determine which learners the parent has
  // in the section and log the first one in. The learner app will show them a review modal for their latest
  // meeting in that section.
  useEffect(() => {
    if (learnersData?.learnersForParentInSection?.length && reviewSection) {
      switchToLearnerMode(
        getTransferToken,
        appState,
        learnersData.learnersForParentInSection[0].uid,
        currentUser,
        reviewSection,
      );
    }
  }, [learnersData, getTransferToken, appState, currentUser, reviewSection]);

  useEffect(() => {
    redirectAfterLogin();
  }, [redirectAfterLogin]);

  return { stripeIsLoading };
}
